import { FilePreviewLink } from "@/components/FilePreviewLink";
import { Module, PATH_PROJECTS_CONTRACTOR_CARD } from "@/constants";
import { FilterType } from "@/lib/filters";
import { readContractorCard } from "@/schemas/contractorCard";
import { ContractorCardsService } from "@/services/contractorCards";
import { ReadContractorCard } from "@/types/contractorCard";
import { Row } from "@tanstack/react-table";
import { useMemo, useCallback } from "react";
import { generatePath } from "react-router-dom";
import { QueryTable } from "@/components/query-table/QueryTable";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";
import { MonthYearTableFilter } from "@/components/data-table/components/MonthYearTableFilter";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { RowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useCreateRowSelectionStore } from "@/components/data-table/hooks/useCreateRowSelectionStore";
import { ContractorCardsDownloadContent } from "./ContractorCardsDownloadContent";

export function ContractorCardsList() {
  const options =
    useCreateDataTableOptions<ReadContractorCard>("contractorCards");

  const queryKey = useDynamicQueryParams("contractorCards", options);

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadContractorCard>) =>
      lastPage.last_item,
    [],
  );

  const fetchList = useCallback(
    async ({ pageParam }: { pageParam?: string }) =>
      ContractorCardsService.getList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [options.filters, options.sorting],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: fetchList,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);
  const store = useCreateRowSelectionStore();
  const rowSelectionContextValue = useMemo(() => ({ store }), [store]);

  const columnOverrides = useMemo(
    () => ({
      card_filename: {
        id: "card_filename",
        cell: ({ row }: { row: Row<ReadContractorCard> }) => (
          <div
            className="flex items-center h-full"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <FilePreviewLink
              s3Key={row.original.card_s3_key}
              displayFileName={row.original.card_filename}
            />
          </div>
        ),
        enableColumnFilter: true,
        enableSorting: false,
        meta: {
          filter: {
            allowedFilterTypes: [
              FilterType.IncludesString,
              FilterType.IsEmpty,
              FilterType.IsNotEmpty,
            ],
          },
        },
      },
    }),
    [],
  );

  return (
    <RowSelectionProvider>
      <DataTableQueryContextProvider value={queryValue}>
        <RowSelectionContext.Provider value={rowSelectionContextValue}>
          <QueryTable<ReadContractorCard>
            schema={readContractorCard}
            name={"contractorCards"}
            query={query}
            options={options}
            columnOverrides={columnOverrides}
            tableFeatures={{
              exportSettings: { moduleName: Module.ContractorCards },
              enableRowSelection: true,
              enableSelectAll: true,
              initialColumnVisibility: {
                card_s3_key: false,
                store_coordinator_name: false,
                supervisor_name: false,
                regional_representative_name: false,
                regional_director_name: false,
              },
            }}
            pathBuilder={(row) =>
              generatePath(PATH_PROJECTS_CONTRACTOR_CARD, {
                contractorCardId: row.id,
              })
            }
          >
            <QueryTable.Header
              breadcrumbLabel={"contractorCards.title.multiple"}
            />
            <div className="flex flex-row gap-2 justify-between items-center py-2">
              <QueryTable.Menubar className="mb-0 grow">
                <QueryTable.Menubar.Tools />
                <QueryTable.Menubar.Columns />
                <QueryTable.Menubar.Settings />
              </QueryTable.Menubar>

              <MonthYearTableFilter
                columnId="period"
                className="shrink-0 grow-0"
                popoverClassName="z-100"
                disabled={query.isFetching}
              />
            </div>
            <QueryTable.Content>
              <div className="flex items-center justify-between gap-4">
                <QueryTable.Status>
                  <QueryTable.SelectedRowsStatus />
                </QueryTable.Status>
                <ContractorCardsDownloadContent />
              </div>
            </QueryTable.Content>
          </QueryTable>
        </RowSelectionContext.Provider>
      </DataTableQueryContextProvider>
    </RowSelectionProvider>
  );
}
